/**
 * PHOTOS §4: the IndexedDB behind `PhotoBackend` - database `nightdrop-photos`, store `photos`, records
 * `{ id, jpeg, at, uploaded }` keyed by id. Kept as thin as it can be: every decision is in `store.ts`, which the
 * tests run on a Map. Where there is no IndexedDB (a private window, node, a blocked origin) it answers null and
 * photos are simply off - never an error, never a gate.
 */

import type { PhotoBackend, PhotoRecord } from './store';

export const PHOTO_DB = 'nightdrop-photos';
export const PHOTO_STORE = 'photos';
const PHOTO_DB_VERSION = 1;
/** An open that neither succeeds nor fails (another tab holding an old version) must not leave photos pending. */
const OPEN_TIMEOUT_MS = 5000;

function asRecord(raw: unknown): PhotoRecord | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  const { id, jpeg, at, uploaded } = r;
  if (typeof id !== 'string' || !(jpeg instanceof Blob) || typeof at !== 'number') return null;
  return { id, jpeg, at, uploaded: uploaded === true };
}

function backendOver(db: IDBDatabase): PhotoBackend {
  // Another tab asking for a newer version must not be blocked by this one.
  db.onversionchange = () => db.close();
  /** One request in its own transaction, resolved when the transaction has committed. */
  const run = <T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> =>
    new Promise<T>((resolve, reject) => {
      const tx = db.transaction(PHOTO_STORE, mode);
      const request = work(tx.objectStore(PHOTO_STORE));
      tx.oncomplete = () => resolve(request.result);
      tx.onerror = () => reject(tx.error ?? new Error('photo store'));
      tx.onabort = () => reject(tx.error ?? new Error('photo store'));
    });
  return {
    get: async (id) => asRecord(await run('readonly', (s) => s.get(id))),
    put: async (record) => void (await run('readwrite', (s) => s.put({ ...record }))),
    delete: async (id) => void (await run('readwrite', (s) => s.delete(id))),
    all: async () => {
      const rows = await run('readonly', (s) => s.getAll());
      return rows.map(asRecord).filter((r): r is PhotoRecord => r !== null);
    },
  };
}

/** Opens (or creates) the database; resolves null wherever it cannot. Never rejects. */
export function openPhotoBackend(): Promise<PhotoBackend | null> {
  return new Promise((resolve) => {
    let factory: IDBFactory | undefined;
    try {
      factory = typeof indexedDB === 'undefined' ? undefined : indexedDB;
    } catch {
      // Reading `indexedDB` itself throws on an origin with storage blocked.
      factory = undefined;
    }
    if (factory === undefined) {
      resolve(null);
      return;
    }
    const timer = setTimeout(() => resolve(null), OPEN_TIMEOUT_MS);
    const done = (backend: PhotoBackend | null): void => {
      clearTimeout(timer);
      resolve(backend);
    };
    try {
      const request = factory.open(PHOTO_DB, PHOTO_DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(PHOTO_STORE)) db.createObjectStore(PHOTO_STORE, { keyPath: 'id' });
      };
      request.onsuccess = () => done(backendOver(request.result));
      request.onerror = () => done(null);
    } catch {
      done(null);
    }
  });
}
